import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { VerificationAgencyContactsService } from '../../../Component/Api/VerificationAgency/VerificationAgencyContacts/VerificationAgencyContactsService';
import AppMultiSelect from '../../../Component/Common/Components/FormValidationInputs/AppMultiSelect';
import { IDropDownOptions } from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import { SmallLoading } from '../../../Component/Common/Components/Loading/IntermediateLoading';
import { MDBox } from '../../../Component/Common/Components/MDBox';
import { VerificationAgencyContacts } from '../../../Component/Model/Dto/VerificationAgency/VerificationAgencyContacts';
import { SimpleFullNameFormatter } from '../../../Component/Model/helpers';
import { CustomDialogTitle } from '../../Common/Custom/CustomDialogTitle';
import { FeedbackValidationEmailSchema } from "./FeedbackValidationSchema";

interface IProps {
    onCloseModal: () => void;
    showModal: boolean;
    vaClientId: number;
    isLoading: boolean;
    onSend: (contacts: VerificationAgencyContacts.ContactList[]) => Promise<void>;
}
export const FeedbackEmailDialog = (props: IProps) => {
    const [clientContactNames, setClientContactNames] = React.useState<VerificationAgencyContacts.ContactListWithIndex[]>();
    const [contactDropdownOptions, setContactDropdownOptions] = React.useState<IDropDownOptions[]>([]);
    const [isLoadingContacts, setIsLoadingContacts] = React.useState<boolean | null>(null);
    const history = useHistory();
    const { control, handleSubmit, formState: { isSubmitting } } = useForm({
        mode: 'all',
        resolver: yupResolver(FeedbackValidationEmailSchema)
    });

    const handleDataSubmit = async (data: FieldValues) => {
        const selected = data.EmailTo as number[];
        const selectedContacts = clientContactNames?.filter(item => selected.some(s => item.id == s)) as VerificationAgencyContacts.ContactList[];
        props.onSend(selectedContacts);
    }

    const GetContactNames = async () => {
        if (props.vaClientId > 0) {
            setIsLoadingContacts(true);
            try {
                const data = await VerificationAgencyContactsService.GetContactNames(props.vaClientId);
                setClientContactNames(data.data.map((item, index) => {
                    return { id: index, contactId: item.contactId, contactEmail: item.contactEmail, contactFirstName: item.contactFirstName, contactLastName: item.contactLastName }
                }))

                const contactNames: IDropDownOptions[] = data.data.map((item, index) => {
                    return { id: index, value: (SimpleFullNameFormatter(item.contactFirstName, item.contactLastName) + ' (' + item.contactEmail + ')').toString() }
                }) ?? [];
                setContactDropdownOptions(contactNames);
            } catch (error) {
                console.log(error);
            }
            finally {
                setIsLoadingContacts(false);
            }
        }
    }

    const handleAddClick = () => {
        history.push(`/company/details/contacts`);
    }

    useEffect(() => {
        GetContactNames();
    }, [props.vaClientId]);

    return (
        <>
            <Dialog disableEnforceFocus open={props.showModal && isLoadingContacts === false} onClose={props.onCloseModal} aria-labelledby="form-dialog-title" >
                <form id='SendFeedbackEmail' onSubmit={handleSubmit(handleDataSubmit)}>
                    <CustomDialogTitle id="individual-dialog-title" onClose={props.onCloseModal}>
                        {contactDropdownOptions.length > 0 ? "Select contact(s) to send feedback form to:" : "No email addresses available"}
                    </CustomDialogTitle>
                    <DialogContent >
                        <MDBox>
                            <Grid>
                                <Grid mt={1} pl={2} pr={2} xs={10}>
                                    {contactDropdownOptions.length > 0 ?
                                        <AppMultiSelect control={control} name="EmailTo" label="Email To" options={contactDropdownOptions} size="small" disabled={props.isLoading} />
                                        :
                                        <Typography variant='body2'>Please add a contact on the contacts page.</Typography>
                                    }
                                </Grid>
                            </Grid>
                            {props.isLoading &&
                                <div>
                                    <SmallLoading />
                                    Sending ...
                                </div>}
                        </MDBox>
                    </DialogContent>
                    <DialogActions>
                        {!props.isLoading && contactDropdownOptions.length > 0 &&
                            <LoadingButton loading={isSubmitting} variant='contained' form='SendFeedbackEmail' type="submit" color='primary'>
                                Send
                            </LoadingButton>
                        }
                        {!props.isLoading && contactDropdownOptions.length === 0 &&
                            <Button onClick={handleAddClick} variant='contained' color='primary' >
                                Go to Contacts Page
                            </Button>
                        }
                    </DialogActions>
                </form>
            </Dialog>
        </>
    )
}
